import {WIDTH, HEIGHT} from 'components/effects/plane/constants.js';
import {randomize} from './randomize.js';
import {setEffectParameterValues} from 'actions/index.js';
import {value} from 'nft/random.js';
import {shuffle} from "utils/ShuffleArray.js"

const NUM_VARIATIONS = 16;
const NUM_MUTATIONS = 3;

export default superclass => class extends superclass  {
    variations = [];
    generation = 0;
    _bestSolutions = [];

    firstReproduction(numVariations) {
        let coords = [];                
        this._parameters.forEach(
            p => coords[p.id] = p.getCoordinates());
        this.variations = this.generateVariations(coords, numVariations);
    }

    clearGenetic() {
        this.generation = 0;
        this.variations = [];
        this.categories = undefined;
        this._bestSolutions = [];
        this.selectedVariation = undefined;
    }
    
    getBestCopy() {
        return this.bestSolutions.map(
                v => ({... v}));
    }

    reproduceSnapshots(degree=13.0/16.0, numVariations=NUM_VARIATIONS) {
        let _snapshots = [...this.effect.snapshots].slice(0, 16);
        let snapshots = [];
        for (let snap of _snapshots) {
            let snapshot = {};
            snap.forEach(
                ({parameter, coordinates}) =>
                    snapshot[parameter.id] = coordinates);
            snapshots.push(snapshot);
        }

        let coords = [];                
        this._parameters.forEach(
            p => coords[p.id] = p.getCoordinates());
        let variations = this.generateVariations(coords, numVariations - snapshots.length);
        this.variations = [... snapshots, ... variations];
        this.generation++;
        this._bestSolutions = [];
    }

    unsupervised(iterations=16) {
        let numMutations = 8;
        let numVariations = 64;
        let numBest = this.effect.snapshots.length || 8;
        for (let i=0; i < iterations; i++) {
            if (i==0) {
                if (this.effect.snapshots.length > 0) {
                    this.reproduceSnapshots();
                    for (let i=0; i < numBest; i++ ){
                        this.selectBest(i);
                    }
                    continue;
                } else {
                    this.reproduce(undefined, numVariations, numMutations);
                }
            } else {
                this.reproduce(undefined, numVariations, numMutations);
            }
            let toReplace = Math.floor(numBest * value());
            for (let j=0; j < numBest; j++) {
                this.selectBest(j);
            }
            let best = numBest + Math.floor(value() * (numVariations - numMutations - 3));
            this.selectBest(best);
            this.selectedVariation = best;

            let snapshot = [];
            for (let id in this.variations[best]) {
                let coordinates = this.variations[best][id];
                let parameter = this._parameters.find(x => x.id === id);
                snapshot.push(
                    {
                        parameter,
                        coordinates,
                        min: parameter.min,
                        max: parameter.max
                    });

            }
            this.effect.snapshots.push(snapshot);
            // now choose the elite
        }
        this.effect.snapshots = [... this.effect.snapshots];
        this.currentSnapshotParameter.set(this.snapshots.length - 1);
        this.clearGenetic();
    }

    reproduce(degree=13.0/16.0, numVariations=NUM_VARIATIONS, numMutations=NUM_MUTATIONS) {
        if (this.generation === 0) {
            this.firstReproduction(numVariations);
        } else {
            let bestSolutions = this.getBestCopy();
            if (bestSolutions.length === 0) {
                return;
            }
            let newSolutions = [... bestSolutions]; // copy over best solutions
            let nonBestSolutions = [... this.nonBestSolutions];
            let children = [];
            for (let i=newSolutions.length; i < nonBestSolutions.length - numMutations; i++) {
                // breed each "non-best" solution with a best solution
                let eliteSolution = randChoose(bestSolutions);
                let nonEliteSolution = randChoose(nonBestSolutions);
                let bredSolution = this.breed(eliteSolution, nonEliteSolution);
                children.push(bredSolution);
                newSolutions.push(bredSolution);
            }
            let mutations = [];
            for (let i=0; i < numMutations; i++) {
                // randomly generate some mutations based off the best solution
                let goodSolution = randChoose(bestSolutions);
                let mutation = this.generateVariation(goodSolution, degree);
                mutations.push(mutation);
                newSolutions.push(mutation);
            }
            this.categories = {
                elite: bestSolutions,
                children,
                mutations
            };
            this.variations = newSolutions;
        }

        this.generation++;
        this._bestSolutions = [];
        if(this.generation == 1) {
            this.selectBest(0);
            this.reproduce();
        }
    }

    selectBest(num) {
        if (this._bestSolutions.includes(num)) {
            // then we need to remove that
            this._bestSolutions = this._bestSolutions.filter(
                x => x !== num);
        } else {
            // we just add it
            this._bestSolutions.push(num);
        }
    }

    selectVariation(num, time, rampTime=0) {
        let variation = this.variations[num];
        let bulk = {};
        for (let _id in variation) {
            let param = this._parameters.find(p => p.id === _id);
            if (!param) {
                continue;
            }
            let {x,y} = variation[_id];
            let {id, value} = param.setCoordinates(x, y, time, rampTime, true);
            this.effect.clearParameter(param, undefined, true);
            bulk[id] = value;
        }
        this.track.sequencerEngine.dispatch(
            setEffectParameterValues(bulk));
        this.selectedVariation = num;
    }

    // breeds 2 solutions together
    /**
     * a: elite
     * b : non-elite
     */
    breed(a, b) {
        let solution = {};
        for (let {id} of this._parameters) {
            let flip = value();
            // bias towards elite
            if (flip < 0.6) {
                solution[id] = {... a[id]};
            } else {
                solution[id] = {... b[id]};
            }
        }
        return solution;
    }

    get bestSolutions() {
        return this._bestSolutions.map(
            x => this.variations[x]);
    }

    get nonBestSolutions() {
        let solutions = [];
        for (let i in this.variations) {
            if (!this.bestSolutions.includes(i)) {
                solutions.push(this.variations[i]);
            }
        }
        return solutions;
    }

    // initializes variations based on the current group
    generateVariations(coordinates, numVariations) {
        let degree = 1.0/8.0;
        // always have the "starting point" as the initial variation
        let variations = [coordinates];
        for (let i=1; i < numVariations; i++) {
            let variation = this.generateVariation(coordinates, degree);
            variations.push(variation);
            degree += 1.0/32.0;
        }
        return variations;
    }

    generateVariation(coordinates, degree) {
        let variation = {};
        for (let p of this._parameters) {
            if (!coordinates[p.id]) {
                continue;
            }
            let {x,y} = coordinates[p.id];
            variation[p.id] = {
                x: randomize(x, 0, WIDTH, degree),
                y: randomize(y, 0, HEIGHT, degree)
            };
        }
        return variation;
    };

}

const randChoose = (x) => x[Math.floor(value() * x.length)];
